import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, TrendingUp, Clock, DollarSign } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const INDUSTRY_SHORTCUTS = [
  { slug: 'healthcare', label: 'Healthcare' },
  { slug: 'property-management', label: 'Property Management' },
  { slug: 'manufacturing', label: 'Manufacturing' },
  { slug: 'education', label: 'Education' },
];

// Standard assumptions. Tuned for a generic mid-market AP team — industry
// pages have vertical-specific versions with richer inputs.
const MINUTES_SAVED_PER_INVOICE = 10; // typical manual-to-automated gain
const HOURS_PER_EXCEPTION_RESOLVED = 0.5;
const EXCEPTION_PREVENTION_RATE = 0.75; // conservative — site claims up to 90%; calculator uses 75% for realistic estimates

const ROICalculator = () => {
  const [monthlyInvoices, setMonthlyInvoices] = useState(2500);
  const [costPerInvoice, setCostPerInvoice] = useState(14);
  const [exceptionRate, setExceptionRate] = useState(25);
  const [staffRate, setStaffRate] = useState(32);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const results = useMemo(() => {
    const hoursSavedPerMonth = (monthlyInvoices * MINUTES_SAVED_PER_INVOICE) / 60;
    const laborSavingsMonthly = hoursSavedPerMonth * staffRate;

    const exceptionsPerMonth = monthlyInvoices * (exceptionRate / 100);
    const exceptionsPrevented = exceptionsPerMonth * EXCEPTION_PREVENTION_RATE;
    const exceptionSavingsMonthly =
      exceptionsPrevented * HOURS_PER_EXCEPTION_RESOLVED * staffRate;

    const processingSavingsMonthly =
      monthlyInvoices * costPerInvoice * 0.5; // 50% processing-cost reduction

    const totalMonthly =
      laborSavingsMonthly + exceptionSavingsMonthly + processingSavingsMonthly;
    const annualSavings = Math.max(0, totalMonthly * 12);
    const hoursSavedAnnual = hoursSavedPerMonth * 12;
    const fteEquivalent = hoursSavedAnnual / 2080; // 2080 work hours/year

    return {
      annualSavings,
      hoursSavedAnnual,
      fteEquivalent,
    };
  }, [monthlyInvoices, costPerInvoice, exceptionRate, staffRate]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AP Automation ROI Calculator | EZ Cloud"
        description="Estimate your accounts payable automation savings in 30 seconds. Enter invoice volume, exception rate, and staff cost — get annualized savings, hours saved, and FTE equivalent."
        canonical="https://ezcloud.ai/resources/roi-calculator"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/roi-calculator' },
          { name: 'ROI Calculator', url: 'https://ezcloud.ai/resources/roi-calculator' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-5xl">
        <header className="mb-10 max-w-3xl">
          <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-3">
            <Calculator className="w-4 h-4" aria-hidden />
            Resource · ROI Calculator
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How much can EZ Cloud save your AP team?
          </h1>
          <p className="text-lg text-gray-600">
            A 30-second estimate based on typical mid-market AP workloads. For a closer fit, use the version on your <Link to="/industries/healthcare" className="text-accent underline">industry page</Link>.
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Inputs */}
          <Card className="p-6 bg-white border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Your numbers</h2>
            <div className="space-y-6">
              <div>
                <Label htmlFor="invoices" className="text-gray-700 mb-2 block">
                  Monthly invoice volume
                </Label>
                <Input
                  id="invoices"
                  type="number"
                  min={0}
                  value={monthlyInvoices}
                  onChange={(e) => setMonthlyInvoices(Math.max(0, Number(e.target.value) || 0))}
                  className="bg-white border-gray-300 text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1">Invoices processed per month across all entities.</p>
              </div>
              <div>
                <Label htmlFor="cost" className="text-gray-700 mb-2 block">
                  Cost per invoice ($)
                </Label>
                <Input
                  id="cost"
                  type="number"
                  min={0}
                  step="0.01"
                  value={costPerInvoice}
                  onChange={(e) => setCostPerInvoice(Math.max(0, Number(e.target.value) || 0))}
                  className="bg-white border-gray-300 text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Fully loaded — labor, paper, postage, storage. Industry average is $10–$20.
                </p>
              </div>
              <div>
                <Label htmlFor="exceptions" className="text-gray-700 mb-2 block">
                  Exception rate (%)
                </Label>
                <Input
                  id="exceptions"
                  type="number"
                  min={0}
                  max={100}
                  value={exceptionRate}
                  onChange={(e) =>
                    setExceptionRate(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                  }
                  className="bg-white border-gray-300 text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Share of invoices that hit a PO mismatch, missing data, or approval issue.
                </p>
              </div>
              <div>
                <Label htmlFor="rate" className="text-gray-700 mb-2 block">
                  Fully-loaded AP staff rate ($/hr)
                </Label>
                <Input
                  id="rate"
                  type="number"
                  min={0}
                  step="0.01"
                  value={staffRate}
                  onChange={(e) => setStaffRate(Math.max(0, Number(e.target.value) || 0))}
                  className="bg-white border-gray-300 text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include benefits and overhead (typically 1.3× base hourly).
                </p>
              </div>
            </div>
          </Card>

          {/* Results */}
          <Card className="p-6 bg-accent/10 border-accent/30">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Your estimated savings</h2>

            <div className="space-y-5 mb-8">
              <div>
                <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                  <DollarSign className="w-4 h-4" aria-hidden />
                  Annual savings
                </div>
                <div className="text-4xl font-bold text-accent">
                  ${Math.round(results.annualSavings).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                  <Clock className="w-4 h-4" aria-hidden />
                  Hours saved per year
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {Math.round(results.hoursSavedAnnual).toLocaleString()} hrs
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                  <TrendingUp className="w-4 h-4" aria-hidden />
                  FTE equivalent
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {results.fteEquivalent.toFixed(2)}
                </div>
              </div>
            </div>

            <ROIResultsCapture
              industry="General (ROI Calculator)"
              annualSavings={results.annualSavings}
              inputs={{
                'Monthly invoices': monthlyInvoices.toLocaleString(),
                'Cost per invoice': `$${costPerInvoice}`,
                'Exception rate': `${exceptionRate}%`,
                'Staff hourly rate': `$${staffRate}`,
              }}
              buttonLabel="Email My ROI Report"
            />

            <p className="text-xs text-gray-500 mt-6">
              * Estimates use conservative industry averages. Your actual results may vary based on ERP, vendor mix, and current automation level.
            </p>
          </Card>
        </div>

        {/* Industry shortcuts */}
        <section aria-labelledby="tailored" className="border-t border-white/10 pt-12">
          <h2 id="tailored" className="text-2xl font-semibold text-gray-900 mb-2">
            Want a tighter estimate?
          </h2>
          <p className="text-gray-600 mb-6">
            Our industry-specific calculators factor in workflows unique to your vertical — fund accounting for schools, 3-way match for manufacturing, utility auditing for property management.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {INDUSTRY_SHORTCUTS.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors text-gray-900"
              >
                <span className="font-medium">{ind.label}</span>
                <ArrowRight className="w-4 h-4 text-accent" aria-hidden />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ROICalculator;
